import type { PlacedImage } from '@/tools/pdf-engine';

/** PDF points per millimetre (1 pt = 1/72 inch). */
export const PT_PER_MM = 72 / 25.4;

export type QuarterTurn = 0 | 90 | 180 | 270;

/** An image as the screen shows it: centre, size and clockwise angle, in points from the top-left. */
export interface DisplayedBox {
  readonly cx: number;
  readonly cy: number;
  readonly width: number;
  readonly height: number;
  readonly angle: number;
}

export function normaliseTurn(degrees: number): QuarterTurn {
  return ((((Math.round(degrees / 90) * 90) % 360) + 360) % 360) as QuarterTurn;
}

/** The page's size as displayed: width and height swap for a quarter turn. */
export function displayedSize(
  width: number,
  height: number,
  turn: QuarterTurn,
): { width: number; height: number } {
  return turn === 90 || turn === 270 ? { width: height, height: width } : { width, height };
}

/**
 * The box drawn on the page as displayed (rotated `turn` degrees clockwise), expressed in the
 * unrotated page the PDF stores, as the engine takes it: bottom-left origin, the box before its own
 * rotation, rotation clockwise about the centre. `page` is the unrotated visible area (CropBox).
 */
export function toPdfPlacement(
  box: DisplayedBox,
  page: {
    readonly x?: number;
    readonly y?: number;
    readonly width: number;
    readonly height: number;
  },
  turn: QuarterTurn,
): Pick<PlacedImage, 'x' | 'y' | 'width' | 'height' | 'rotation'> {
  const W = page.width;
  const H = page.height;
  // The centre, from the display's top-left to the unrotated page's PDF coordinates.
  let cx: number;
  let cy: number;
  switch (turn) {
    case 0:
      cx = box.cx;
      cy = H - box.cy;
      break;
    case 90:
      cx = box.cy;
      cy = box.cx;
      break;
    case 180:
      cx = W - box.cx;
      cy = box.cy;
      break;
    case 270:
      cx = W - box.cy;
      cy = H - box.cx;
      break;
  }
  // The visible area may not start at the page's origin (a CropBox, or a MediaBox not at 0,0).
  return {
    x: (page.x ?? 0) + cx - box.width / 2,
    y: (page.y ?? 0) + cy - box.height / 2,
    width: box.width,
    height: box.height,
    // The page is shown turned `turn` clockwise, so on the unrotated page the image turns less.
    rotation: (((box.angle - turn) % 360) + 360) % 360,
  };
}
